import React, { useEffect, useRef, useState } from 'react';
import { strings } from '../constants/strings';
import { colors } from '../constants/colors';
import { Send, Mail } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRecaptchaDialogOpen, setIsRecaptchaDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const recaptchaRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (submitStatus?.type === 'error') {
      setSubmitStatus(null);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Validate form fields
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fill in all required fields'
      });
      return;
    }
    
    // Open reCAPTCHA dialog
    setIsRecaptchaDialogOpen(true);
  };

  const handleRecaptchaSubmit = async () => {
    const recaptchaToken = recaptchaRef.current?.getValue();
    
    if (!recaptchaToken) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please verify you are not a robot' 
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        recaptcha_token: recaptchaToken
      });
      
      if (response.data.success) {
        setSubmitStatus({ 
          type: 'success', 
          message: response.data.message 
        });
        setFormData({ name: '', company: '', email: '', message: '' });
        recaptchaRef.current?.reset();
        setIsRecaptchaDialogOpen(false);
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: 'Something went wrong. Please try again.' 
        });
        recaptchaRef.current?.reset();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to send message. Please try again later.' 
      });
      recaptchaRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding contact-section" ref={sectionRef}>
      <div className="contact-background">
        <div className="floating-circle circle-1"></div>
        <div className="floating-circle circle-2"></div>
        <div className="floating-circle circle-3"></div>
      </div>
      
      <div className="container">
        <div className="contact-grid">
          <div className={`contact-info ${isVisible ? 'slide-in-left' : ''}`}>
            <h2 className="section-title">{strings.contact.title}</h2>
            <p className="large-text" style={{ marginTop: '24px' }}>{strings.contact.subtitle}</p>
            
            <div className="contact-email-box">
              <Mail size={24} style={{ color: colors.accent }} />
              <a href={`mailto:${strings.contact.email}`}>{strings.contact.email}</a>
            </div>
            
            <div className="contact-decoration"></div>
          </div>
          
          <div className={`contact-form-wrapper ${isVisible ? 'slide-in-right' : ''}`}>
            <form onSubmit={handleFormSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">{strings.contact.formFields.name}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="company">{strings.contact.formFields.company}</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">{strings.contact.formFields.email}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">{strings.contact.formFields.message}</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="form-input"
                  disabled={isSubmitting}
                  minLength="10"
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                className="btn-primary btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : strings.contact.ctaButton}
                <Send size={20} />
              </button>
              
              {submitStatus && (
                <div className={`submit-status ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* reCAPTCHA Dialog */}
      <Dialog open={isRecaptchaDialogOpen} onOpenChange={setIsRecaptchaDialogOpen}>
        <DialogContent className="recaptcha-dialog">
          <DialogHeader>
            <DialogTitle className="dialog-title">Verify You're Human</DialogTitle>
            <DialogDescription className="dialog-description">
              Please complete the verification below to submit your message.
            </DialogDescription>
          </DialogHeader>
          
          <div className="recaptcha-dialog-content">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              theme="light"
            />
            
            <button 
              onClick={handleRecaptchaSubmit}
              className="btn-primary btn-verify"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Form'}
              <Send size={18} />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Contact;
